import { useState, useMemo, useCallback } from 'react';
import { Box, Stack } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { useDispatch } from 'react-redux';
import { SearchInput } from '../../../common/SearchInput';
import { ArtifactTemplateCardGrid } from './ArtifactTemplateCardGrid';
import { ArtifactTemplatePreviewModal } from './ArtifactTemplatePreviewModal';
import { artifactTemplates, ArtifactTemplate } from './artifactTemplatesData';
import {
  setAdvancedMode,
  setArtifactsViewActive,
  setPendingInputText,
} from '../../../aiAgentCenter/agentsHomeReducer';
import { AgentInputMode } from '../../../aiAgentCenter/agent-input/agentInputModeTypes';
import useAgentsChats from '../../../aiAgentCenter/useAgentsChats';
import { AiAgent } from '../../../aiAgentCenter/aiAgentsConsts';

interface BuiltBySweepTabProps {
  activeAgent: AiAgent;
}

export const BuiltBySweepTab = ({ activeAgent }: BuiltBySweepTabProps) => {
  const dispatch = useDispatch();
  const { setAgentClickHandler } = useAgentsChats();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<ArtifactTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) {
      return artifactTemplates;
    }
    const query = searchQuery.toLowerCase();
    return artifactTemplates.filter(
      (template) =>
        template.title.toLowerCase().includes(query) ||
        template.subtitle.toLowerCase().includes(query),
    );
  }, [searchQuery]);

  const currentIndex = useMemo(() => {
    if (!selectedTemplate) return -1;
    return filteredTemplates.findIndex((t) => t.id === selectedTemplate.id);
  }, [selectedTemplate, filteredTemplates]);

  const handleCardClick = useCallback((template: ArtifactTemplate) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  }, []);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setSelectedTemplate(filteredTemplates[currentIndex - 1]);
    }
  }, [currentIndex, filteredTemplates]);

  const handleNext = useCallback(() => {
    if (currentIndex < filteredTemplates.length - 1) {
      setSelectedTemplate(filteredTemplates[currentIndex + 1]);
    }
  }, [currentIndex, filteredTemplates]);

  const handleUseTemplate = useCallback(
    (template: ArtifactTemplate) => {
      // Set the pending input text with the template prompt
      dispatch(setPendingInputText(template.prompt));
      // Enable artifact mode
      dispatch(setAdvancedMode({ advancedMode: AgentInputMode.ArtifactMode }));
      // Close artifacts view
      dispatch(setArtifactsViewActive(false));
      // Navigate to agent (new chat)
      setAgentClickHandler({ agent: activeAgent });
      // Close the modal
      handleCloseModal();
    },
    [dispatch, setAgentClickHandler, activeAgent, handleCloseModal],
  );

  return (
    <>
      {/* Template count and Search */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body" color={colors.grey[700]}>
          Showing {filteredTemplates.length} Artifacts
        </Typography>
        <Box sx={{ width: '240px' }}>
          <SearchInput
            withFixedMagnifyingGlassIcon
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClearButtonClick={() => setSearchQuery('')}
            size="default"
          />
        </Box>
      </Stack>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredTemplates.length === 0 ? (
          <Stack height="100%" justifyContent="center" alignItems="center" gap={2}>
            <Typography variant="h3-medium" color={colors.grey[800]}>
              No results found
            </Typography>
            <Typography variant="body" color={colors.grey[700]}>
              Try changing your search term
            </Typography>
          </Stack>
        ) : (
          <ArtifactTemplateCardGrid templates={filteredTemplates} onCardClick={handleCardClick} />
        )}
      </Box>

      {/* Preview Modal */}
      <ArtifactTemplatePreviewModal
        open={isModalOpen}
        template={selectedTemplate}
        onClose={handleCloseModal}
        onUseTemplate={handleUseTemplate}
        onPrevious={handlePrevious}
        onNext={handleNext}
        hasPrevious={currentIndex > 0}
        hasNext={currentIndex < filteredTemplates.length - 1}
      />
    </>
  );
};
